//
//	+-----------------------------------------------+
//	| Cardboard data model objects                  |
// 	+-----------------------------------------------+

/**
 * @file cardboard/models.js
 * @desc This contains model objects that encapsulate the results of the various
 * requests made to the WebFOCUS and/or JSON backends.
 * @author Geoffrey Pursell
 */

define('cardboard/models',["jquery", "underscore", "backbone", "backbone-documentmodel", "rsvp",
        "cardboard/mixins", "cardboard/collections"
    ],
    function($, _, Backbone, BackboneDocumentModel, RSVP,
        Mixins, CardboardCollections) {

        console.log("MODULE: cardboard/models");

        /**
         * @exports cardboard/models
         * @desc Cardboard model objects: Contains the model objects that describe
         * underlying data sources in cardboard
         */
        var CardboardModels = {},
            Promise = RSVP.Promise;

        //------------------------------------------------------------
        // === Data Models											 |
        //------------------------------------------------------------

        /**
         * @class
         * @classdesc Base Model class; common stuff needed by our models
         * @augments Backbone.Model
         * @mixes module:cardboard/mixins.ModelMixin
         * @mixes module:cardboard/mixins.ModelAndCollectionMixin
         */
        CardboardModels.Model = Backbone.Model.extend( /** @lends module:cardboard/models.Model.prototype */ {});

        _.extend(CardboardModels.Model.prototype, Mixins.ModelMixin, Mixins.ModelAndCollectionMixin);

        /**
         * @class
         * @classdesc Base DocumentModel class; we need this for detailed report
         * JSON documents that we want to automatically turn their nested arrays
         * into proper collections.
         * @augments Backbone.DocumentModel
         * @mixes Models.ModelMixin
         * @mixes Models.ModelAndCollectionMixin 
         */
        CardboardModels.DocumentModel = Backbone.DocumentModel.extend( /** @lends module:cardboard/models.DocumentModel.prototype */ {});

        _.extend(CardboardModels.DocumentModel.prototype, Mixins.ModelMixin, Mixins.ModelAndCollectionMixin);

        /**
         * @class
         * @classdesc Report; support methods that abstract away differences in the
         * report format between cardboard implementations
         * @augments Models.Model
         * @mixes Mixins.ModelMixin 
         */
        CardboardModels.Report = CardboardModels.Model.extend( /** @lends module:cardboard/models.Report.prototype */ {
            // we support both old and new report list formats.

            getDisplayName: function() {
                return this.get("displayName") || this.get("short");
            },
            getShortDescription: function() {
                return this.get("shortDescription") || this.get("text");
            }
        });

        /**
         * @class
         * @classdesc FilterData: add some info methods to Model to help out with
         * extracting menu information from the funky filter decision tree. This is
         * needed to load filter data that doesn't come from WebFOCUS. All we need
         * is the FilterDataMixin.
         * @augments Models.Model
         */
        CardboardModels.FilterData = CardboardModels.Model.extend({} /** @lends module:cardboard/models.FilterData.prototype */
            // Holy cow, will this work? It might, since all we want is what's on
            // the filter data mixin.
        );
        _.extend(CardboardModels.FilterData.prototype, Mixins.FilterDataMixin);

        /**
         * @class
         * @classdesc DerivativeFilterData: for the generic filter data, which needs
         * to be re-filtered any time a new report is selected and put into its
         * own (simple!) model for its view to watch
         * @augments Models.Model
         */
        CardboardModels.DerivativeFilterData = CardboardModels.Model.extend({ /** @lends module:cardboard/models.DerivativeFilterData.prototype */
            // nothing to do here! it's been fetched already!
            fetch: function() {
                this.trigger("sync", this);
                return null;
            }
        });
        _.extend(CardboardModels.DerivativeFilterData.prototype, Mixins.FilterDataMixin);

        /**
         * @class
         * @classdesc FilterProducer class; take our new-API filter sources that
         * transmit to us every possible filter combination, which is much more
         * compact than transmitting entire decision trees, as in the old API, and
         * transform it into the decision trees that our view objects expect to work
         * with, match the format of the old one.
         * @augments module:cardboard/models.Model
         */
        CardboardModels.FilterProducer = CardboardModels.Model.extend( /** @lends module:cardboard/models.FilterProducer.prototype */ {
            /** constructor */
            initialize: function(attributes, options) {
                var setup = _({}).extend(options).value();

                this.dataSourceInfo = setup;
                this.loaded = false;
                this.isCat = false;

                /** the "source" filter data given to us under the new API scheme;
                 * we will populate filters and options based on these.
                 * @type Object
                 */
                this.collections = {};

                this.urls = setup.urls;
                if (setup.defaultParams) {
                    this.setParams(setup.defaultParams || {});
                }

                this.catchErrors();
            },

            /** return an array of built-out menu info objects in their default
             * state. This now just calls getCurrentMenus with an empty parameter
             * list (AKA no user selections), forcing it to barf out the default
             * menu values
             * @returns {Array} an array of the default built-out menu info
             * objects. */
            getDefaultMenus: function() {
                return this.getCurrentMenus({});
            },

            /** return an array of built-out menu info objects that's correctly
             * filtered based on current selections.
             * @param {Object} a hash of the current state of the menu selections
             * @returns {Array} */
            getCurrentMenus: function(currentSelections) {
                var menus = this._buildFilters(currentSelections);
                return menus;
            },

            /** override fetch to get and get the web service URLs provided by the
             * "urls" option, store them as nested collections, and then produce a
             * clean filter decision tree from them in the format the rest of the
             * app expects.
             * @returns {Promise} a promise that resolves when both sources have
             * been loaded and the filter decision tree produced */
            fetch: function() {
                var self = this;
                this.collections = {};
                _(this.urls).each(function(url, key) {
                    self.collections[key] = new CardboardCollections.Collection([], {
                        url: url
                    });
                });
             
                _(this.collections).invoke("setParams", this.params);
                _(this.urls).each(function(url, key){
        
                    if((url == "/ibi_apps/WFServlet?IBIAPP_app=ECLDSwebsvc&IBIF_ex=ecldsws_getdata_filter_ECSEto3rdGrade_nbr3.fex") || (url == "/ibi_apps/WFServlet?IBIAPP_app=ECLDSwebsvc&IBIF_ex=ecldsws_getdata_filter_ECSEto3rdGrade_nbr2.fex")|| (url == "/ibi_apps/WFServlet?IBIAPP_app=ECLDSwebsvc&IBIF_ex=ecldsws_getdata_filter_maternalTraitsAttendance_nbr2.fex"))
                      self.collections[key].params = null;
                });
                var loadingPromises = _(this.collections).invoke("fetch").value();

                this.requestDonePromise = Promise.all(loadingPromises)
                    .then(function() {
                        self.loaded = true;
                    })
                    .catch(function() {
                        console.log("ERROR fetching filter URLs.");
                    });

                return this.requestDonePromise;
            },

            /** build the filter decision tree, given our more abstract inputs from
             * the new API.
             * @returns {Object}
             * @private */
            _buildFilters: function(currentSelectionCodes) {

                var self = this;
               var filter;
                var filterOrder = this.dataSourceInfo.filterOrder;
           
                    _filterForReport = _(this.collections.filterForReport.toJSON())
                    .groupBy("filterUrl")
                    .pairs()
                    .sortBy(function(filterGroup) {
                        if (filterOrder) {
                            var filterUrl = filterGroup[0],
                                position = filterOrder.indexOf(filterUrl);
                            return (position >= 0 ? position : 99999);
                        }
                        return 99999;
                    });
               
                    var output = [],
                    optionCombos = this.collections.filterOptionsForOrg,
                    // start with the full list of available combos. This comes to
                    // us already filtered for organization.

                    availableCombos = (optionCombos ? optionCombos.toJSON() : null),
                    masterFilterSelectedOptionCode, masterFilterCode;

                _filterForReport.each(function(filterMenu) {
                    var filterId = filterMenu[0],
                        allOptionsForThisFilter = filterMenu[1],
                        firstOption = allOptionsForThisFilter[0],
                        selectedOptionCodeForThisFilter = currentSelectionCodes[filterId],
                        optionFilterResult, firstAvailableOptionAfterFiltering;

                    optionFilterResult = self._filterOptionsArray(allOptionsForThisFilter, availableCombos, filterId, masterFilterCode, masterFilterSelectedOptionCode);

                    output.push({
                        paramName: filterId,
                        displayName: firstOption.filterLabel,
                        subDisplayName: firstOption.filterSubLabel,
                        options: optionFilterResult.options
                    });

                    // as we iterate, the list of available options combos is
                    // whittled down, and the slave becomes the next menu's master
                    firstAvailableOptionAfterFiltering = optionFilterResult.options[0];

                    // the option request by currentSelectionCodes may not be
                    // available after filtering this menu. deal with it.
                    if (_(optionFilterResult.options).pluck("code").contains(selectedOptionCodeForThisFilter)) {
                        masterFilterSelectedOptionCode = selectedOptionCodeForThisFilter;
                    } else if (firstAvailableOptionAfterFiltering) {
                        masterFilterSelectedOptionCode = firstAvailableOptionAfterFiltering.code;
                    }
                    masterFilterCode = filterId;
                    availableCombos = optionFilterResult.remainingCombos;
                });

                return output;
            },
            _filterOptionsArray: function(allOptions, availableCombosForThisFilter, filterName, masterFilterName, masterFilterSelectedOptionCode) {
                var combosNarrowedByMasterFilterSelection, narrowedOptions, availableOptionCodesForThisFilter;
                var comboMasterKeyName = masterFilterName + "Code",
                    comboCurrentKeyName = filterName + "Code",
                    predicate = {};

                // if this data source provides us no collection of available
                // combos, we have no filtering to do at all
                if (availableCombosForThisFilter) {
                    // if there's a constraint imposed on the contents of this menu by a
                    // value selected in the preceding menu (considered the "master" of
                    // this menu due to it being above the current on in the UI), we
                    // first narrow the list of options based on that
                    if (masterFilterSelectedOptionCode) {
                        predicate[comboMasterKeyName] = masterFilterSelectedOptionCode;
                        availableCombosForThisFilter = _(availableCombosForThisFilter).where(predicate).value();
                    }

                    // pick out all the available values for this filter from the
                    // available combos collection
                    availableOptionCodesForThisFilter = _(availableCombosForThisFilter).pluck(comboCurrentKeyName).uniq().value();

                    // ...and narrow the options list based on that
                    narrowedOptions = _(allOptions).filter(function(option) {
                        return _(availableOptionCodesForThisFilter).contains(option.code);
                    }).value();
                }

                return {
                    options: narrowedOptions || allOptions,
                    remainingCombos: combosNarrowedByMasterFilterSelection || availableCombosForThisFilter
                };
            }
        });

        return CardboardModels;
    });
